import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthLayout } from "@/components/AuthLayout";
import { Field, BigButton } from "@/components/Field";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-5 w-5"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
);

export default function Login() {
  const { user, login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await login(form.email, form.password);
      toast.success(`Bem-vindo(a), ${data.user.name}!`);
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Entrar" subtitle="Acesse sua conta Fazenda Digital.">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <Field label="E-mail" id="email" type="email" required autoComplete="email" placeholder="seu@email.com"
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" />
        <Field label="Senha" id="password" type="password" required autoComplete="current-password" placeholder="••••••••"
          value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" />
        <div className="flex justify-end">
          <Link to="/esqueci-senha" className="text-sm font-semibold text-[#C86D51] hover:underline py-2" data-testid="forgot-password-link">
            Esqueci minha senha
          </Link>
        </div>
        <BigButton type="submit" disabled={busy} data-testid="login-submit-button">
          {busy && <Loader2 className="h-5 w-5 animate-spin" />} Entrar
        </BigButton>
      </form>
      <div className="flex items-center gap-3 my-6 text-xs uppercase tracking-wider text-[#5C6B66]">
        <span className="h-px flex-1 bg-[#E2DDD3]" /> ou <span className="h-px flex-1 bg-[#E2DDD3]" />
      </div>
      <BigButton variant="outline" type="button" onClick={loginWithGoogle} data-testid="google-login-button">
        <GoogleIcon /> Entrar com Google
      </BigButton>
      <p className="mt-7 text-center text-[#5C6B66]">
        É cliente e ainda não tem conta?{" "}
        <Link to="/cadastro" className="font-semibold text-[#1E4D2B] hover:underline" data-testid="register-link">Cadastre-se</Link>
      </p>
    </AuthLayout>
  );
}
